import React, { useState, useEffect } from "react";
import { company, leadSources } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Phone, Mail, MapPin, Printer, Clock } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { leadsApi, track, gaEvent } from "../api";
import PayerStrip from "../components/PayerStrip";
import useSeo from "../hooks/useSeo";

const Contact = () => {
  useSeo({
    title: "Contact & Schedule | Home Care in Ohio | Caregivers Alliance Homecare (614) 526-1548",
    description: "Contact Caregivers Alliance Homecare in Columbus, Ohio. Request care, refer a patient, or book a free consultation online. DODD & MyCare Ohio certified. Call (614) 526-1548.",
    keywords: "contact home care Ohio, schedule home care consultation Ohio, refer a patient Ohio, DODD PASSPORT home care contact, accepting new patients Ohio",
    path: "/contact",
  });
  const { toast } = useToast();

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin.includes("calendly.com") && e.data?.event === "calendly.event_scheduled") {
        gaEvent("book_appointment", { method: "calendly" });
        track("calendly_booked");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  const [fam, setFam] = useState({ name: "", contact: "", email: "", careType: "", note: "", source: "Family" });
  const [ref, setRef] = useState({ name: "", contact: "", email: "", org: "", source: "Hospital", note: "" });
  const [busy, setBusy] = useState(false);

  const submitFam = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await leadsApi.create({
        name: fam.name,
        contact: fam.contact,
        email: fam.email || (fam.contact.includes("@") ? fam.contact : ""),
        phone: fam.contact.includes("@") ? "" : fam.contact,
        source: fam.source,
        org: "—",
        note: `${fam.careType ? fam.careType + " — " : ""}${fam.note}`,
      });
      toast({ title: "Request sent!", description: "Our care team will contact you shortly." });
      track("care_request_submitted");
      setFam({ name: "", contact: "", email: "", careType: "", note: "", source: "Family" });
    } catch {
      toast({ title: "Something went wrong", description: "Please call us at (614) 526-1548.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const submitRef = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await leadsApi.create({
        name: ref.name,
        contact: ref.contact,
        email: ref.email || (ref.contact.includes("@") ? ref.contact : ""),
        phone: ref.contact.includes("@") ? "" : ref.contact,
        org: ref.org,
        source: ref.source,
        note: ref.note,
      });
      toast({ title: "Referral received!", description: "Thank you — our intake team will follow up quickly." });
      track("referral_submitted");
      setRef({ name: "", contact: "", email: "", org: "", source: "Hospital", note: "" });
    } catch {
      toast({ title: "Something went wrong", description: "Please call us at (614) 526-1548.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="brand-gradient text-white">
        <div className="mx-auto max-w-7xl px-4 py-16">
          <h1 className="font-display font-extrabold text-4xl md:text-5xl">Contact Us</h1>
          <p className="mt-4 text-white/90 max-w-2xl">
            We're here to help. Reach out for a free consultation, or refer a patient for home care.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="mx-auto max-w-7xl px-4 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: Phone, label: "Phone", value: company.phone, href: `tel:${company.phoneRaw}` },
              { icon: Printer, label: "Fax", value: company.fax },
              { icon: Mail, label: "Email", value: company.email, href: `mailto:${company.email}` },
              { icon: MapPin, label: "Address", value: `${company.address}, ${company.city}` },
              { icon: Clock, label: "Hours", value: company.hours },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="flex items-start gap-4 bg-[#F7FBFC] rounded-xl p-5 border border-slate-100">
                  <div className="brand-gradient h-11 w-11 rounded-xl grid place-items-center text-white shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-sm text-slate-500">{c.label}</div>
                    {c.href ? (
                      <a href={c.href} className="font-semibold text-[#0E2A33] hover:text-[#0E5A72]">{c.value}</a>
                    ) : (
                      <div className="font-semibold text-[#0E2A33]">{c.value}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-3">
            <Tabs defaultValue="family" className="w-full">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="family">Request Care</TabsTrigger>
                <TabsTrigger value="referral">Refer a Patient</TabsTrigger>
              </TabsList>

              <TabsContent value="family">
                <form onSubmit={submitFam} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-4 mt-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Your name</Label>
                      <Input required value={fam.name} onChange={(e) => setFam({ ...fam, name: e.target.value })} className="mt-1" placeholder="Jane Doe" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input required value={fam.contact} onChange={(e) => setFam({ ...fam, contact: e.target.value })} className="mt-1" placeholder="(614) 555-0100" />
                    </div>
                  </div>
                  <div>
                    <Label>Email (to receive our reply)</Label>
                    <Input data-testid="fam-email-input" type="email" value={fam.email} onChange={(e) => setFam({ ...fam, email: e.target.value })} className="mt-1" placeholder="you@email.com" />
                  </div>
                  <div>
                    <Label>Type of care needed</Label>
                    <Input value={fam.careType} onChange={(e) => setFam({ ...fam, careType: e.target.value })} className="mt-1" placeholder="Personal care, companion care..." />
                  </div>
                  <div>
                    <Label>Tell us more</Label>
                    <Textarea value={fam.note} onChange={(e) => setFam({ ...fam, note: e.target.value })} className="mt-1" rows={4} placeholder="Schedule, location, any specific needs..." />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full bg-[#2E9E6A] hover:bg-[#23875A] text-white rounded-full h-12">{busy ? "Sending..." : "Send Request"}</Button>
                </form>
              </TabsContent>

              <TabsContent value="referral">
                <form onSubmit={submitRef} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-7 space-y-4 mt-4">
                  <p className="text-sm text-slate-500">For hospital & nursing home case managers and discharge planners.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Patient name</Label>
                      <Input required value={ref.name} onChange={(e) => setRef({ ...ref, name: e.target.value })} className="mt-1" placeholder="Patient full name" />
                    </div>
                    <div>
                      <Label>Best contact</Label>
                      <Input required value={ref.contact} onChange={(e) => setRef({ ...ref, contact: e.target.value })} className="mt-1" placeholder="Phone or email" />
                    </div>
                  </div>
                  <div>
                    <Label>Your email (to receive our reply)</Label>
                    <Input data-testid="ref-email-input" type="email" value={ref.email} onChange={(e) => setRef({ ...ref, email: e.target.value })} className="mt-1" placeholder="you@organization.com" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Facility / organization</Label>
                      <Input value={ref.org} onChange={(e) => setRef({ ...ref, org: e.target.value })} className="mt-1" placeholder="e.g. OhioHealth Riverside" />
                    </div>
                    <div>
                      <Label>Referral source</Label>
                      <Select value={ref.source} onValueChange={(v) => setRef({ ...ref, source: v })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {leadSources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Care needs / notes</Label>
                    <Textarea value={ref.note} onChange={(e) => setRef({ ...ref, note: e.target.value })} className="mt-1" rows={4} placeholder="Discharge date, services needed, diagnosis notes..." />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full bg-[#0E5A72] hover:bg-[#0A4557] text-white rounded-full h-12">{busy ? "Submitting..." : "Submit Referral"}</Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>

      <PayerStrip compact />

      <section className="section brand-gradient-soft">
        <div className="mx-auto max-w-5xl px-4">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[#2E9E6A] font-semibold uppercase tracking-wide text-sm">Book Online</span>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-[#0E2A33] mt-2">
              Schedule a free consultation
            </h2>
            <p className="text-slate-600 mt-4">
              Pick a time that works for you and we'll walk you through how we can help your loved one.
            </p>
          </div>
          <div className="mt-8 rounded-2xl overflow-hidden shadow-lg border border-slate-100 bg-white">
            <iframe
              title="Schedule a free home care consultation — Caregivers Alliance Homecare"
              src={`${company.calendly}?hide_gdpr_banner=1&primary_color=0E5A72`}
              className="w-full border-0"
              style={{ height: "720px", minHeight: "660px" }}
              loading="lazy"
              data-testid="calendly-embed"
            />
          </div>
          <div className="text-center mt-4">
            <a href={company.calendly} target="_blank" rel="noopener noreferrer" className="text-[#0E5A72] font-medium hover:underline">
              Trouble booking? Open the scheduler in a new tab →
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
